import { rotuloDeNivel } from '@orc/shared';
import { ehRodape } from './classe-do-item.js';

/**
 * Formatação pt-BR para exibição — Story 2.3.
 *
 * ## Por que este arquivo existe
 *
 * `moedaBr`, `quantidadeBr` e `percentualBr` moravam em `estado/gerador.ts`,
 * junto da store Zustand do gerador de orçamento. Importá-las de lá custa o
 * módulo inteiro: o `create<EstadoGerador>()` roda no import, e uma tela que
 * não gera orçamento nenhum passaria a carregar toda a lógica do gerador no
 * seu chunk.
 *
 * Elas foram **movidas** para cá, sem uma vírgula de mudança no corpo, e o
 * `gerador.ts` as reexporta — nenhum consumidor precisou mudar de import. O
 * que ficou lá é o que de fato pertence ao gerador: as conversões de fronteira
 * de formulário (`percentualParaFracao`, `campoParaFrete`), que só fazem
 * sentido com um campo editável do outro lado.
 *
 * ## A regra que vale para todas
 *
 * Recebem **string**, nunca `number` de cálculo: o valor já vem pronto do
 * servidor e só é convertido na hora de exibir. `Number` aqui é o último passo
 * do pipeline, não parte dele. O front não recalcula dinheiro.
 *
 * ## E por que nenhuma delas escreve "R$"
 *
 * `toLocaleString` com `style: 'currency'` insere um **espaço não-quebrável**
 * (U+00A0) depois do símbolo. Todo `getByText('R$ 6.000,00')` digitado com
 * espaço normal falharia, e a largura da coluna mudaria em relação às telas que
 * já existem. O símbolo é escrito no JSX, ao lado do número.
 */

/**
 * Formata um decimal em string para pt-BR.
 *
 * Recebe **string**, nunca `number` de cálculo: o valor já vem pronto do
 * servidor e só é convertido na hora de exibir. `Number` aqui é o último passo
 * do pipeline, não parte dele.
 */
export function moedaBr(valor: string | null | undefined, casas = 2): string {
  if (valor === null || valor === undefined) return '—';
  const n = Number(valor);
  if (!Number.isFinite(n)) return String(valor);
  return n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

/**
 * Quantidade — mostra o que foi digitado, sem inventar nem esconder casas.
 *
 * Dívida D-01: quantidade passava pelo `moedaBr`, que **fixa duas casas**.
 * `13,7` virava `13,70` (ruído) e `2,505` virava `2,51` — este último é o que
 * dói: a tela mostrava um número que não bate com a calculadora do vendedor nem
 * com o que o servidor usou na conta. Aqui as casas são as que o valor tem, até
 * quatro (a escala das colunas de medida no banco).
 */
export function quantidadeBr(valor: string | null | undefined): string {
  if (valor === null || valor === undefined) return '—';
  const n = Number(valor);
  if (!Number.isFinite(n)) return String(valor);
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
}

/**
 * Fração `0..1` → percentual legível. `'0.5000'` vira `'50%'`.
 *
 * ⚠️ Devolve `'0%'` para nulo **e** para zero — não distingue "sem valor" de
 * "zero por cento". Quem precisar dessa diferença não pode usar esta função.
 */
export function percentualBr(fracao: string | null | undefined): string {
  if (fracao === null || fracao === undefined) return '0%';
  const n = Number(fracao);
  if (!Number.isFinite(n) || n === 0) return '0%';
  return `${(n * 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
}

/**
 * Como a forma de pagamento é dita a QUEM LÊ — uma definição só.
 *
 * Internamente o modo a prazo se chama `cartao` (é o multiplicador 1,0471), e
 * o valor no banco **não muda**. O que muda é a palavra na tela: a Dantas Lobo
 * chama isso de *a prazo*, e "Cartão" prometia uma forma de pagamento
 * específica que o cliente pode não ter contratado.
 *
 * O PDF já dizia "A prazo" desde 28/08; a tela ficou para trás por uma sessão.
 * Estava escrito em **quatro** lugares — lista, editor, seletor e dashboard —,
 * e quatro cópias do mesmo rótulo é como se diverge sem perceber. Agora é aqui.
 */
export const ROTULO_PAGAMENTO: Record<string, string> = {
  avista: 'À vista',
  cartao: 'A prazo',
};

/** O rótulo, ou a própria chave quando ela é desconhecida — nunca esconde. */
export function rotuloPagamento(tipo: string | null | undefined): string {
  if (tipo === null || tipo === undefined) return '—';
  return ROTULO_PAGAMENTO[tipo] ?? tipo;
}

/**
 * Como a TABELA do orçamento é dita a quem lê — S12, mesmo desenho do
 * `ROTULO_PAGAMENTO`.
 *
 * O código (`CPF`, `RT10`, `DEL_CREDERE`) é chave do banco e não muda. O que
 * muda é a palavra: o cliente conhece as tabelas da Villagres pelo nome —
 * Engenharia (a 122, sem RT), RT 5 (a 123, só rodapé) e RT 10 (a 136, piso e
 * vinílico). As duas últimas moram no MESMO quadrante `RT10` desde a S10.
 *
 * O ORC-2026-0053 saiu em CPF com RT zerado, R$ 85 abaixo do pedido Villagres:
 * "CPF" não dizia ao vendedor que ali não há RT embutido.
 */
export const ROTULO_TABELA: Record<string, string> = {
  CPF: 'Engenharia',
  RT10: 'RT 5 / RT 10',
  DEL_CREDERE: 'Del Credere',
};

/** O rótulo, ou o próprio código quando ele é desconhecido — nunca esconde. */
export function rotuloTabela(code: string | null | undefined): string {
  if (code === null || code === undefined) return '—';
  return ROTULO_TABELA[code] ?? code;
}

/** Uma linha sob cada opção do seletor: o que a tabela faz com o RT. */
export const EXPLICACAO_TABELA: Record<string, string> = {
  CPF: 'preço de engenharia, sem RT da Villagres; você define RT e frete',
  RT10: 'RT já embutido pela Villagres (RT 5 rodapé, RT 10 piso/vinílico); o sistema não soma nada',
  DEL_CREDERE: 'tabela Del Credere da Villagres; o nível (3% a 7%) é escolhido no orçamento',
};

interface ItemDeOrigem {
  referencia: string;
  unidadeVenda?: 'caixa' | 'barra' | undefined;
  priceSourceKey: string;
  precoManual: boolean;
}

/**
 * De onde saiu o preço base do item, em palavras — S12, CA-5.
 *
 * O quadrante `RT10` guarda DUAS tabelas da Villagres desde a S10: a 123
 * ("RT 5", só rodapé) e a 136 ("RT 10", piso e vinílico). Só o item diz qual.
 *
 * "RT 5" exige uma coluna `com_*`: são as que a S10 trouxe da 123. Um rodapé
 * emitido antes dela (ORC-2026-0052, `preco_7`) saiu da tabela de maio, e
 * atribuí-lo à RT 5 seria contar uma origem que ele não teve. Chave sem rótulo
 * (`preco_7`) não aparece: um código cru não diz nada a quem vende.
 */
export function origemDoPreco(tabela: string, item: ItemDeOrigem): string {
  if (item.precoManual) return 'Preço manual';

  const daS10 = item.priceSourceKey.startsWith('com_');
  const qual =
    tabela === 'CPF'
      ? 'Tabela Engenharia (sem RT)'
      : tabela === 'RT10'
        ? ehRodape(item)
          ? daS10
            ? 'Tabela RT 5 (rodapé)'
            : `Tabela ${rotuloTabela(tabela)}`
          : 'Tabela RT 10'
        : `Tabela ${rotuloTabela(tabela)}`;

  const coluna = rotuloDeNivel(item.priceSourceKey);
  return coluna === undefined || coluna === item.priceSourceKey
    ? qual
    : `${qual} · coluna ${coluna}`;
}
